<template>
  <div class="selection-bar" :class="rootClasses">
    <div class="selection">
      <span v-if="count > 0 || active" class="count">{{ count }}</span>
      <span class="label">{{ labelComputed }}</span>
      <button v-if="count > 0" class="button clear-selection" @click.prevent="handleClearSelection">
        <svg-icon name="Cross24" kind="action" />
        {{ t("button_clear") }}
      </button>
    </div>
    <div class="action-tools">
      <slot name="action-tools" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Props
interface Props {
  count?: number;
  label?: string;
  active?: boolean;
}

const { count = 0, label = "", active = false } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  clear: [];
}>();

// Composables
const { t } = useI18n();

// Computed
const labelComputed = computed(() => {
  return label || t("table_rows_selected");
});

const rootClasses = computed(() => {
  return {
    _active: count > 0 || active,
  };
});

// Methods
const handleClearSelection = () => {
  emit("clear");
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.selection-bar {
  align-items: center;
  background-color: $inverse-02;
  color: $inverse-01;
  height: 0;
  display: none;
  border-radius: 10px;
  z-index: z-map("selection-bar");
  transform: translateZ(0);

  &._active {
    height: 44px;
    display: flex;
  }

  .selection {
    @include carbon--type-style("body-long-01", true);
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    padding: $spacing-03 $spacing-05;

    .count,
    .label {
      @include carbon--type-style("body-short-01", true);
    }

    .count {
      margin-right: $spacing-02;
      font-weight: bold;
    }

    .clear-selection {
      display: flex;
      align-items: center;
      gap: $spacing-02;
      margin-left: $spacing-05;
      color: $inverse-link;
      @include carbon--type-style("button-02");

      &:hover {
        &:not([disabled]) {
          color: $ui-01;
        }
      }

      &:active {
        color: $ui-01;
      }

      [stroke] {
        stroke: currentColor;
      }

      [fill] {
        fill: currentColor;
      }
    }
  }

  .action-tools {
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: $spacing-05;

    ul {
      display: flex;
      align-items: center;
    }

    li {
      &:not(:first-child) {
        .button,
        .tooltip-button {
          margin-left: $spacing-05;
          padding-left: $spacing-05;
          border-left: 1px solid $ui-05;
        }
      }
    }

    .button {
      @include carbon--type-style("button-02");
      display: flex;
      align-items: center;
      color: $inverse-link;

      &[disabled] {
        cursor: not-allowed;
        opacity: 0.5;
      }

      &:hover,
      &:active {
        &:not([disabled]) {
          color: $ui-01;
        }
      }

      .sls-icon {
        flex: 0 0 auto;
        margin: 0 $spacing-02 $spacing-01 0;

        [stroke] {
          stroke: currentColor;
        }

        [fill] {
          fill: currentcolor;
        }
      }

      span {
        @include line-clamp(2);
        max-width: 11rem;
        line-height: 14px;
        text-align: left;
      }
    }

    .tooltip-button {
      background: none;
      height: auto;
      min-width: unset;
      min-height: unset;
      padding: 0;
      line-height: inherit;
      border-radius: 0;

      &:hover {
        background: none;
      }

      .bx--tooltip__trigger {
        padding: 0;
        cursor: not-allowed;

        // To show tooltip above selection bar as tooltip bg and selection bar are the same colour
        &.bx--tooltip--a11y {
          &.bx--tooltip--top {
            &::before {
              top: -1rem;
            }

            .bx--assistive-text {
              top: -20px;
            }
          }

          &.bx--tooltip--bottom {
            &::before {
              bottom: -1rem;
            }

            .bx--assistive-text {
              bottom: -20px;
            }
          }
        }

        .tooltip-button-label,
        .sls-icon {
          opacity: 0.5;
        }

        .tooltip-button-label {
          @include carbon--type-style("button-02");
          max-width: 10rem;
          line-height: 14px;
          text-align: left;
        }

        .sls-icon {
          flex: 0 0 auto;
          margin: 0 $spacing-02 $spacing-01 0;
        }
      }
    }
  }
}
</style>
